import {
    Box,
    Drawer,
    Grid,
    IconButton,
    Stack,
    Divider,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { 
    GoogleMap, 
    MarkerF,
} from '@react-google-maps/api';
import _ from "lodash";
import { When } from "react-if";
import { useEffect, useRef, useState } from "react";

import { ReportTypes } from "../../types";
import { LatLng } from "../event-map";
import { markerConstants } from "../../constants/marker.constants";
import dayjs from "dayjs";

interface Props {
    onClose(): void,
    visible: boolean,
    report: ReportTypes.Model
}

export const ReportDetailsDrawer: React.FC<Props> = ({
    onClose,
    visible,
    report,
}) => {
    const mapRef = useRef<google.maps.Map | null>(null);

    const isMountedRef = useRef<boolean>(false);

    const [center, setCenter] = useState<LatLng>(
        {
            lat: Number(report?.latitude),
            lng: Number(report?.longitude)
        }
    );
    const [zoom, setZoom] = useState<number>(18);

    const handleMapLoad = (map: google.maps.Map) => {
        mapRef.current = map;
    };

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    return (
        <Drawer
            anchor="bottom"
            open={visible}
            onClose={onClose}
        >
            <Box
                sx={{ minHeight: { xs: "500px", md: "500px" } }}
            >
                <Stack direction={'row'} justifyContent={'space-between'} mt={1} style={{ padding: 10 }}>
                    <Typography fontSize={24} fontWeight={400} textAlign={'left'}>
                        Report Details
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>

                <Divider />

                <Box style={{ padding: 16 }} mt={1}>
                    <Grid container spacing={2}>
                        <Grid container spacing={2} p={2} md={6} sm={12} alignContent={'flex-start'}>
                            <Grid item md={12} sm={12} xs={12}>
                                <Typography mb={2} fontWeight={400}>
                                    Information About The Report
                                </Typography>
                            </Grid>

                            <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    Name:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {report?.name}
                                </Typography>
                            </Grid>
                            <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    Type:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {report?.reportType}
                                </Typography>
                            </Grid>
                            <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    Comment:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {report?.reportComment}
                                </Typography>
                            </Grid>
                            <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    Address:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {report?.address}
                                </Typography>
                            </Grid>
                            <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    Created At:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {dayjs(report?.createdAt).format('MM/DD/YYYY hh:mm A')}
                                </Typography>
                            </Grid>
                            <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    Last Update:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {dayjs(report?.updatedAt).format('MM/DD/YYYY hh:mm A')}
                                </Typography>
                            </Grid>
                        </Grid>

                        <Divider orientation="vertical" flexItem />

                    
                        <Grid container spacing={2} p={2} md={6} sm={12} xs={12} alignContent={'flex-start'}>
                            <When condition={report?.latitude && report?.longitude}>
                                <Grid item md={12} sm={12} xs={12}>
                                    <Typography mb={2} fontWeight={400}>Location</Typography>
                                    <div style={{ width: '100%', height: 400 }}>
                                            <GoogleMap
                                                onLoad={handleMapLoad}
                                                options={{
                                                    disableDefaultUI: true,
                                                    zoomControl: true,
                                                    fullscreenControl: true,
                                                    mapTypeControl: true,
                                                    streetViewControl: true,
                                                    scaleControl: true,
                                                    rotateControl: true,
                                                    clickableIcons: false,
                                                    scrollwheel: true,
                                                    draggable: true,
                                                    draggableCursor: 'default',
                                                    draggingCursor: 'pointer',
                                                    keyboardShortcuts: true,
                                                    disableDoubleClickZoom: false,
                                                    noClear: false,
                                                    styles: [
                                                        {
                                                            featureType: 'poi',
                                                            elementType: 'labels',
                                                            stylers: [
                                                                {
                                                                    visibility: 'off',
                                                                },
                                                            ],
                                                        },
                                                    ],
                                                }}
                                                mapContainerStyle={{ height: '100%', width: '100%' }}
                                                zoom={zoom}
                                                center={center}
                                            >
                                                <MarkerF
                                                    position={{
                                                        lat: Number(report?.latitude),
                                                        lng: Number(report?.longitude)
                                                    }}
                                                    options={{
                                                        icon: {
                                                            url: markerConstants['report'],
                                                            scaledSize: new google.maps.Size(10, 10),
                                                            scale: 1,
                                                            origin: new google.maps.Point(0, 0),
                                                        }
                                                    }}

                                                />
                                            </GoogleMap>
                                    </div>
                                </Grid>
                            </When>
                        </Grid>

                    </Grid>
                </Box>
            </Box>
        </Drawer>
    )
}